// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { createUpdateRequiredFetch } from '@/lib/updateRequiredInterceptor';
import { authStorage } from '@/platform/authStorage';

// Under vitest, fall back to inert placeholders so any module that transitively
// imports this client (e.g. countryConfig -> useOperationalRegions) can be
// imported in tests without a real .env. Never applies to app builds.
const IS_TEST = !!import.meta.env.VITEST;
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || (IS_TEST ? 'http://127.0.0.1:54321' : '');
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || (IS_TEST ? 'test-anon-key' : '');

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
              throw new Error(
                                    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY environment variables. Check your .env file.'
                            );
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Derive the key Supabase JS v2 uses internally: sb-{project-ref}-auth-token
// where project-ref is the subdomain of the Supabase project URL.
export const SUPABASE_AUTH_KEY = `sb-${SUPABASE_URL.replace(/^https?:\/\//, '').split('.')[0]}-auth-token`;

// "Remember me" behaviour:
// - We keep localStorage as the default storage so sessions survive page navigations
//   (sessionStorage is wiped on every hard redirect, breaking the SPA login flow).
// - When the user does NOT tick "Remember me", we schedule a cleanup so the token
      //   is removed from localStorage when the browser is fully closed. This is handled
//   in AuthForm (src/components/auth/AuthForm.tsx): after sign-in without
//   rememberMe, we tag a sessionStorage sentinel value. On the next module
//   load (new tab/session), if the sentinel is absent but the localStorage
//   token exists and was NOT remembered, we remove it.
export const REMEMBER_ME_KEY = 'th_remember_me';

// REMOVED: a module-load block that deleted the auth token whenever
// `th_remember_me === 'false'` and no sessionStorage sentinel was found.
//
// It logged people out. sessionStorage is per-TAB, not per-browser-session,
// so a newly opened tab always starts without the sentinel — the comment
// above gave the confusion away by writing "new tab/session" as if the two
// were the same thing. The token lives in localStorage, which every tab on
// the origin shares. So:
//
//   1. sign in without ticking "Remember me" (the default — AuthForm:66)
//   2. open the app in a second tab, or follow any target="_blank" link
//   3. that tab's module load deletes the shared token
//   4. BOTH tabs are now signed out
//
// Found while trying to verify #798 P2's signed-in path: three attempts in a
// row reported "no session", and the cause was the verification opening a
// second tab. Nothing in src/test pinned any of this.
//
// Deleting the block is deliberately the SMALL fix. It cannot log anyone out
// — it only ever removes a destructive path — and it is trivially revertible.
//
// The cost, stated plainly: "Remember me" now has no effect. It is the lesser
// evil against a checkbox whose real behaviour is "log me out at random", but
// it is not the end state. The correct fix is a storage adapter that routes
// the token to sessionStorage while the box is unticked and to localStorage
// while it is ticked, so the promise enforces itself instead of relying on a
// sentinel to approximate browser lifetime. That touches the auth client's
// storage and needs a real sign-in to test, so it is its own change.


// M2 (brief §2.5): the Android build keeps the session in Keystore-backed
// secure storage, never WebView localStorage; the adapter also migrates M1's
// localStorage state and enforces the idle deadline before the first read.
// A compile-time constant (same expression as `isNative` in src/platform,
// which cannot be imported here: the web platform imports this client), so
// the web bundle keeps `localStorage` and drops the adapter.
const NATIVE_BUILD = import.meta.env.VITE_PLATFORM === 'native';

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
              auth: {
                                    // Web: localStorage, so sessions survive SPA page navigations and hard
                                    // redirects. Native: the Keystore-backed secure store, through the M2
                                    // adapter (src/platform/authStorage.ts → src/lib/nativeAuthStorage.ts).
                      storage: authStorage,
                                    persistSession: true,
                                    autoRefreshToken: true,
                                    flowType: 'pkce',
                                    detectSessionInUrl: true,
              },
              // Every PostgREST response passes through this wrapper so a
              // server refusal carrying SQLSTATE KA001 (update required, D19)
              // raises the native blocking screen whatever the call site.
              // Inert on the web and until the post-M7 retirement migration;
              // it ships now because an installed binary cannot be given a
              // listener later (mobile assessment §R.127).
              global: { fetch: createUpdateRequiredFetch() },
});
